import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@enamelai/design-system'
import { SchemaFormRenderer } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import type { GroupField } from '@components/pages/FormBuilder/types'
import { schemaJsonToGroups } from '@components/pages/FormBuilder/SendingFormsTable/formTemplateToFormData'
import {
  getKioskAssignments,
  type KioskAssignmentItem,
} from '@services/api/forms.api'
import {
  resolveInvitationToken,
  verifyInvitationCode,
} from '@services/api/otp.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import './KioskForm.css'

const INVITATION_OTP_BOX_COUNT = 6
const PUBLIC_FORMS_LOADING_CONTAINER = 'body'

function invitationOtpInputName(index: number): string {
  return `public-invitation-otp-${index}`
}

interface ReadOnlyAssignment {
  assignmentId: string
  name: string
  groups: GroupField[]
  fieldValues: Record<string, string>
  patientName?: string
}

const PublicFormsViewPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const invitationTokenFromUrl = searchParams.get('t') ?? ''
  const hasInvitationToken = invitationTokenFromUrl.trim().length > 0
  const codeFromUrl = searchParams.get('code') ?? ''
  /** Legacy query params for older invitation links. */
  const patientIdFromUrl = searchParams.get('patientId') ?? ''
  const dbNameFromUrl = searchParams.get('dbName') ?? ''
  const clinicIdFromUrl = searchParams.get('clinicId') ?? ''
  const [codeDigits, setCodeDigits] = useState<string[]>(() =>
    Array.from({ length: INVITATION_OTP_BOX_COUNT }, () => ''),
  )
  const [verifyError, setVerifyError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [loadingForms, setLoadingForms] = useState(false)
  const [formsError, setFormsError] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [resolvingLink, setResolvingLink] = useState(hasInvitationToken)
  const resolveLinkSessionRef = useRef(0)
  const loadFormsSessionRef = useRef(0)
  const [entries, setEntries] = useState<ReadOnlyAssignment[]>([])
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (codeFromUrl.trim().length > 0) {
      const digits = codeFromUrl
        .replace(/\D/g, '')
        .slice(0, INVITATION_OTP_BOX_COUNT)
        .split('')
      setCodeDigits((prev) => {
        const next = [...prev]
        digits.forEach((d, i) => {
          if (i < INVITATION_OTP_BOX_COUNT) next[i] = d
        })
        return next
      })
      const updatedParams = new URLSearchParams(searchParams.toString())
      updatedParams.delete('code')
      const nextQuery = updatedParams.toString()
      navigate(
        { pathname: '/forms/public', search: nextQuery ? `?${nextQuery}` : '' },
        { replace: true },
      )
    }
  }, [codeFromUrl, navigate, searchParams])

  useLayoutEffect(() => {
    const invitationToken = invitationTokenFromUrl.trim()
    if (!invitationToken) {
      setLinkError(null)
      setResolvingLink(false)
      return
    }
    const session = ++resolveLinkSessionRef.current
    setResolvingLink(true)
    setLinkError(null)
    showLoading({ container: PUBLIC_FORMS_LOADING_CONTAINER })
    resolveInvitationToken(invitationToken)
      .then(() => {
        if (resolveLinkSessionRef.current !== session) return
        setLinkError(null)
      })
      .catch(() => {
        if (resolveLinkSessionRef.current !== session) return
        setLinkError(
          'This invitation link is invalid or has expired. Please ask the clinic for a new link.',
        )
      })
      .finally(() => {
        if (resolveLinkSessionRef.current !== session) return
        setResolvingLink(false)
        hideLoading(PUBLIC_FORMS_LOADING_CONTAINER)
      })
    return () => {
      resolveLinkSessionRef.current += 1
    }
  }, [hideLoading, invitationTokenFromUrl, showLoading])

  const handleCodeDigitChange = useCallback((index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    setCodeDigits((prev) => {
      const next = [...prev]
      next[index] = digit
      if (digit && index < INVITATION_OTP_BOX_COUNT - 1) {
        const el = document.querySelector(
          `input[name="${invitationOtpInputName(index + 1)}"]`,
        ) as HTMLInputElement | null
        el?.focus()
      }
      return next
    })
    setVerifyError('')
  }, [])

  const handleCodeKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault()
        setCodeDigits((prev) => {
          const next = [...prev]
          next[index] = e.key
          return next
        })
        setVerifyError('')
        if (index < INVITATION_OTP_BOX_COUNT - 1) {
          const el = document.querySelector(
            `input[name="${invitationOtpInputName(index + 1)}"]`,
          ) as HTMLInputElement | null
          el?.focus()
        }
        return
      }
      if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
        const el = document.querySelector(
          `input[name="${invitationOtpInputName(index - 1)}"]`,
        ) as HTMLInputElement | null
        el?.focus()
      }
    },
    [codeDigits],
  )

  const handleCodePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const raw = e.clipboardData.getData('text/plain')
      const digits = raw
        .replace(/\D/g, '')
        .slice(0, INVITATION_OTP_BOX_COUNT)
        .split('')
      if (digits.length === 0) return
      setCodeDigits((prev) => {
        const next = [...prev]
        digits.forEach((d, i) => {
          if (i < INVITATION_OTP_BOX_COUNT) next[i] = d
        })
        return next
      })
      setVerifyError('')
      const focusIndex = Math.min(digits.length, INVITATION_OTP_BOX_COUNT) - 1
      setTimeout(() => {
        const el = document.querySelector(
          `input[name="${invitationOtpInputName(focusIndex)}"]`,
        ) as HTMLInputElement | null
        el?.focus()
      }, 0)
    },
    [],
  )

  const handleVerify = useCallback((): void => {
    const code = codeDigits.join('').trim()
    if (code.length !== INVITATION_OTP_BOX_COUNT) {
      setVerifyError('Please enter the full 6-digit invitation code.')
      return
    }
    const invitationToken = invitationTokenFromUrl.trim()
    if (!invitationToken && !dbNameFromUrl.trim()) {
      setVerifyError(
        'This invitation link is missing. Please use the secure link from your invitation message.',
      )
      return
    }
    setVerifying(true)
    setVerifyError('')
    verifyInvitationCode({
      code,
      ...(invitationToken
        ? { invitation_token: invitationToken }
        : {
            db_name: dbNameFromUrl.trim(),
            ...(patientIdFromUrl && { patient_id: patientIdFromUrl }),
          }),
    })
      .then((result) => {
        setToken(result.access_token)
      })
      .catch(() => {
        setVerifyError('Invalid or expired invitation code.')
      })
      .finally(() => {
        setVerifying(false)
      })
  }, [codeDigits, dbNameFromUrl, invitationTokenFromUrl, patientIdFromUrl])

  useLayoutEffect(() => {
    if (!token) return
    const session = ++loadFormsSessionRef.current
    setLoadingForms(true)
    setFormsError(null)
    showLoading({ container: PUBLIC_FORMS_LOADING_CONTAINER })
    getKioskAssignments(token, {
      ...(patientIdFromUrl && { patientId: patientIdFromUrl }),
      ...(dbNameFromUrl && { dbName: dbNameFromUrl }),
      ...(clinicIdFromUrl && { clinicId: clinicIdFromUrl }),
    })
      .then(({ assignments }) => {
        if (loadFormsSessionRef.current !== session) return
        const mapped: ReadOnlyAssignment[] = assignments.map(
          (item: KioskAssignmentItem) => ({
            assignmentId: item.assignmentId,
            name: item.name,
            groups: schemaJsonToGroups(item.schema_json),
            fieldValues: item.initial_field_values ?? {},
            patientName: item.patient_name,
          }),
        )
        setEntries(mapped)
        setSelectedAssignmentId(mapped[0]?.assignmentId ?? null)
      })
      .catch((err: { response?: { status?: number } }) => {
        if (loadFormsSessionRef.current !== session) return
        if (err?.response?.status === 401) {
          setToken(null)
          setVerifyError(
            'Your viewing session expired. Enter the same invitation code to continue.',
          )
          return
        }
        setFormsError(
          'Unable to load your forms at this time. Please contact the clinic.',
        )
      })
      .finally(() => {
        if (loadFormsSessionRef.current !== session) return
        setLoadingForms(false)
        hideLoading(PUBLIC_FORMS_LOADING_CONTAINER)
      })
    return () => {
      loadFormsSessionRef.current += 1
    }
  }, [
    clinicIdFromUrl,
    dbNameFromUrl,
    hideLoading,
    patientIdFromUrl,
    showLoading,
    token,
  ])

  const selectedEntry = useMemo(
    (): ReadOnlyAssignment | null =>
      selectedAssignmentId
        ? (entries.find((item) => item.assignmentId === selectedAssignmentId) ??
          null)
        : null,
    [entries, selectedAssignmentId],
  )

  return (
    <div className="kiosk-form-page public-forms-page">
      <div className="kiosk-form-container">
        <div className="kiosk-form-title">Patient Forms Access</div>
        {resolvingLink ? null : linkError ? (
          <div className="kiosk-form-body kiosk-form-body-otp">
            <div className="kiosk-form-body-title">Invitation unavailable</div>
            <div className="kiosk-form-otp-subtitle">{linkError}</div>
          </div>
        ) : !token ? (
          <div className="kiosk-form-body kiosk-form-body-otp">
            <div className="kiosk-form-body-title">Enter Invitation Code</div>
            <div className="kiosk-form-otp-subtitle">
              Enter the 6-digit invitation code sent by your clinic to view your
              forms. This code stays valid through the end of your appointment
              day.
            </div>
            <div className="kiosk-form-otp-boxes">
              {codeDigits.map((digit, i) => (
                <input
                  key={invitationOtpInputName(i)}
                  name={invitationOtpInputName(i)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  onPaste={handleCodePaste}
                  className="kiosk-form-otp-box kiosk-form-otp-input"
                  aria-label={`Invitation code digit ${i + 1} of ${INVITATION_OTP_BOX_COUNT}`}
                />
              ))}
            </div>
            {verifyError && (
              <div className="kiosk-form-verify-error">{verifyError}</div>
            )}
            <div className="kiosk-form-otp-actions">
              <Button
                variant="primary"
                size="lg"
                onClick={handleVerify}
                disabled={verifying}
              >
                {verifying ? 'Verifying...' : 'View Forms'}
              </Button>
            </div>
          </div>
        ) : loadingForms ? null : formsError ? (
          <div className="kiosk-form-verify-error">{formsError}</div>
        ) : entries.length === 0 ? (
          <div className="kiosk-form-forms-message-inner">
            No forms are currently assigned to this invitation.
          </div>
        ) : (
          <div className="kiosk-form-body kiosk-form-body-forms">
            <div className="kiosk-form-forms-sidepanel open">
              <div className="kiosk-form-forms-sidepanel-inner">
                {entries.map((entry) => (
                  <button
                    type="button"
                    key={entry.assignmentId}
                    className={`unstyled-button kiosk-form-review-form-item ${
                      selectedAssignmentId === entry.assignmentId
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => setSelectedAssignmentId(entry.assignmentId)}
                  >
                    <span>{entry.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="kiosk-form-forms-content">
              {selectedEntry ? (
                <SchemaFormRenderer
                  groups={selectedEntry.groups}
                  formTitle={selectedEntry.name}
                  fieldValues={selectedEntry.fieldValues}
                  readOnly
                  richTextReadOnly
                  patientName={selectedEntry.patientName}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PublicFormsViewPage
